import { forwardRef } from "react";
import { Box, Checkbox, ListItemText, MenuItem, Select, type SelectChangeEvent, styled, useTheme } from "~/common/mui";
import type { Option } from "~/features/salesPartner/components/SearchCard/SalesPartnerSearchBox";
import { useTranslation } from "react-i18next";

export interface SPFinderFiltersRef {
  reset: () => void;
}

interface SPFinderFiltersProps {
  productCompetencesOptions?: Option[];
  certifiedCompetencesOptions?: Option[];
  selectedProductKeys: string[];
  selectedServiceKeys: string[];
  handleProductChange: SelectHandler;
  handleServiceChange: SelectHandler;
  getLabelByKey: GetLabelByKeyFn;
}

type SelectHandler = (event: SelectChangeEvent<string[]>) => void;

type GetLabelByKeyFn = (key: string, options: Option[]) => string;

const ExtraFilters = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  flex: 1,
  overflow: "hidden",
  [theme.breakpoints.down("tablet")]: {
    marginTop: theme.spacing(3),
    flexDirection: "column",
  },
}));

const ExtraFilter = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const SPFinderFilters = forwardRef<SPFinderFiltersRef, SPFinderFiltersProps>((props, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    productCompetencesOptions = [],
    certifiedCompetencesOptions = [],
    selectedProductKeys,
    selectedServiceKeys,
    handleProductChange,
    handleServiceChange,
    getLabelByKey,
  } = props;

  const filterSx = {
    width: 300,
    [theme.breakpoints.down("tablet")]: {
      width: "calc(100vw - 48px)",
    },
  };

  return (
    <ExtraFilters>
      <ExtraFilter>
        <p>{t("partnerFinder.search.productFilter")}</p>
        <Select
          size="small"
          multiple
          displayEmpty
          value={selectedProductKeys}
          onChange={handleProductChange}
          sx={filterSx}
          renderValue={(selected: string[]) => {
            if (selected.length === 0) {
              return t("partnerFinder.search.productFilterPlaceholder");
            }
            return selected.map((key: string) => getLabelByKey(key, productCompetencesOptions)).join(", ");
          }}
        >
          {productCompetencesOptions.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              <Checkbox checked={selectedProductKeys.includes(key)} edge="start" />
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        </Select>
      </ExtraFilter>
      <ExtraFilter>
        <p>{t("partnerFinder.search.serviceFilter")}</p>
        <Select
          size="small"
          multiple
          displayEmpty
          value={selectedServiceKeys}
          onChange={handleServiceChange}
          sx={filterSx}
          renderValue={(selected: string[]) => {
            if (selected.length === 0) {
              return t("partnerFinder.search.serviceFilterPlaceholder");
            }
            return selected.map((key: string) => getLabelByKey(key, certifiedCompetencesOptions)).join(", ");
          }}
        >
          {certifiedCompetencesOptions.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              <Checkbox checked={selectedServiceKeys.includes(key)} edge="start" />
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        </Select>
      </ExtraFilter>
    </ExtraFilters>
  );
});

export default SPFinderFilters;
